import React from 'react';
import Icon from '../AppIcon';

const classeInput = (errore, disabilitato, extra = '') => `
  w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all ${extra}
  ${disabilitato
    ? 'opacity-50 cursor-not-allowed bg-muted/20 border border-border/30'
    : errore
      ? 'bg-red-500/5 border-2 border-red-500/40 focus:border-red-500/60 focus:ring-2 focus:ring-red-500/10'
      : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'}
`;

/** Contenitore comune: label, marcatore di obbligatorietà, errore accanto alla label, suggerimento. */
export const FieldShell = ({ label, name, obbligatorio, errore, sublabel, suggerimento, children }) => (
  <div className="flex flex-col gap-1.5 w-full group">
    <div className="flex flex-wrap justify-between items-center gap-x-3 gap-y-1 px-1">
      <label
        htmlFor={name}
        className={`text-[10px] font-black uppercase tracking-[.15em] transition-colors select-none ${
          errore ? 'text-red-500' : 'text-muted-foreground/60 group-focus-within:text-primary'
        }`}
      >
        {label}
        {obbligatorio && <span className="text-primary ml-0.5" aria-hidden="true">•</span>}
        {sublabel && (
          <span className="ml-1.5 text-muted-foreground/30 normal-case font-bold text-[9px] tracking-normal">
            {sublabel}
          </span>
        )}
      </label>
      {errore && (
        <span role="alert" className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1 animate-in fade-in duration-200">
          <Icon name="AlertCircle" size={9} />
          {errore}
        </span>
      )}
    </div>
    {children}
    {suggerimento && !errore && (
      <p className="px-1 text-[10px] font-bold text-muted-foreground/50">{suggerimento}</p>
    )}
  </div>
);

export const TextField = ({
  label, name, value, onChange, errore, obbligatorio, disabilitato, placeholder,
  sublabel, suggerimento, type = 'text', mono = false, maxLength, autoFocus = false,
}) => (
  <FieldShell label={label} name={name} obbligatorio={obbligatorio} errore={errore} sublabel={sublabel} suggerimento={suggerimento}>
    <input
      id={name}
      name={name}
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabilitato}
      maxLength={maxLength}
      autoFocus={autoFocus}
      aria-invalid={!!errore}
      className={classeInput(errore, disabilitato, mono ? 'font-mono tracking-tight' : '')}
    />
  </FieldShell>
);

export const NumberField = ({
  label, name, value, onChange, errore, obbligatorio, disabilitato, placeholder,
  sublabel, suggerimento, suffisso, min, max, step = 'any',
}) => (
  <FieldShell label={label} name={name} obbligatorio={obbligatorio} errore={errore} sublabel={sublabel} suggerimento={suggerimento}>
    <div className="relative">
      <input
        id={name}
        name={name}
        type="number"
        inputMode="decimal"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onWheel={(e) => e.currentTarget.blur()}
        placeholder={placeholder}
        disabled={disabilitato}
        min={min}
        max={max}
        step={step}
        aria-invalid={!!errore}
        className={classeInput(errore, disabilitato, suffisso ? 'pr-12' : '')}
      />
      {suffisso && (
        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] font-black text-muted-foreground/40 pointer-events-none uppercase">
          {suffisso}
        </span>
      )}
    </div>
  </FieldShell>
);

/** opzioni: [{ value, label, disabled? }] */
export const SelectField = ({
  label, name, value, onChange, opzioni = [], errore, obbligatorio, disabilitato,
  segnaposto = '— Seleziona —', sublabel, suggerimento,
}) => (
  <FieldShell label={label} name={name} obbligatorio={obbligatorio} errore={errore} sublabel={sublabel} suggerimento={suggerimento}>
    <select
      id={name}
      name={name}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabilitato}
      aria-invalid={!!errore}
      className={classeInput(errore, disabilitato, 'cursor-pointer')}
    >
      <option value="">{segnaposto}</option>
      {opzioni.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
      ))}
    </select>
  </FieldShell>
);

export const TextareaField = ({
  label, name, value, onChange, errore, obbligatorio, disabilitato, placeholder,
  sublabel, suggerimento, righe = 3,
}) => (
  <FieldShell label={label} name={name} obbligatorio={obbligatorio} errore={errore} sublabel={sublabel} suggerimento={suggerimento}>
    <textarea
      id={name}
      name={name}
      rows={righe}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabilitato}
      aria-invalid={!!errore}
      className={classeInput(errore, disabilitato, 'resize-none')}
    />
  </FieldShell>
);

/** Interruttore a barra intera, stesso stile del ToggleSwitch del modal articolo CCO. */
export const ToggleField = ({ label, sublabel, checked, onChange, disabilitato }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabilitato}
    onClick={() => onChange(!checked)}
    className={`flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 w-full text-left disabled:opacity-50 ${
      checked
        ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15'
        : 'bg-muted/30 border-border/40 hover:border-border/60'
    }`}
  >
    <div className={`relative shrink-0 w-10 h-5 rounded-full transition-colors duration-300 ${checked ? 'bg-emerald-500' : 'bg-muted/80 border border-border/50'}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-300 ${checked ? 'left-5' : 'left-0.5'}`} />
    </div>
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className={`text-[11px] font-black uppercase tracking-widest transition-colors ${checked ? 'text-emerald-500' : 'text-muted-foreground/50'}`}>
        {label}
      </span>
      {sublabel && (
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/30">{sublabel}</span>
      )}
    </div>
  </button>
);

/** Sezione con titolo e filetto, stesso stile di "Sezione" nel modal articolo CCO. */
export const FormSection = ({ titolo, icona, children }) => (
  <div className="flex flex-col gap-5 p-5 md:p-6 rounded-3xl bg-muted/20 border border-border/40">
    <div className="flex items-center gap-3">
      {icona && <Icon name={icona} size={14} className="text-primary/60" />}
      <span className="text-[11px] font-black uppercase tracking-[.2em] text-primary/70">{titolo}</span>
      <div className="flex-1 h-px bg-border/20" />
    </div>
    <div className="grid gap-5">{children}</div>
  </div>
);

/** Riquadro informativo (avvisi in forma). tono: info | warn */
export const InfoBox = ({ tono = 'info', icona, children }) => {
  const stili = tono === 'warn'
    ? 'bg-amber-500/10 border-amber-500/30 text-amber-500'
    : 'bg-primary/5 border-primary/15 text-primary/70';
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-2xl border ${stili}`}>
      <Icon name={icona || (tono === 'warn' ? 'AlertTriangle' : 'Info')} size={14} className="shrink-0 mt-0.5" />
      <p className="text-[10px] font-black uppercase tracking-widest leading-relaxed">{children}</p>
    </div>
  );
};
